import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, uploadPhoto } from "@/lib/api";
import { fmtDate, fmtDateTime, Loading, ProStatusBadge } from "@/components/common";
import BackNav from "@/components/BackNav";
import CemeteryMap from "@/components/CemeteryMap";

export default function ProIntervento() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [photo, setPhoto] = useState("");
  const [report, setReport] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmPos, setConfirmPos] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get(`/pro/interventions/${id}`).then((r) => setItem(r.data));
  useEffect(() => { load().catch(() => {}); }, [id]);

  if (!item) return <Loading />;

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      setPhoto(await uploadPhoto(file));
      toast.success("Fotografia caricata.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8" data-testid="pro-intervention-detail">
      <BackNav />

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl text-navy">{item.service}</h1>
          <p className="text-sm text-muted-soft mt-1">Cliente: {item.customer_name}</p>
          {item.order_type === "ricorrenza" && (
            <p data-testid="ricorrenza-badge" className="mt-2 inline-block text-[11px] font-bold uppercase tracking-wide text-[#2f5d3e] bg-[#e8f0eb] rounded-full px-3 py-1">
              Consegna ricorrenza{item.preferred_date ? ` · prevista per il ${fmtDate(item.preferred_date)}` : ""}
            </p>
          )}
        </div>
        <ProStatusBadge status={item.status} />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="card-soft p-6 space-y-4 text-sm">
          <p className="eyebrow">Dettagli intervento</p>
          <dl className="space-y-3">
            <div><dt className="label-soft">Luogo</dt><dd className="text-navy font-medium">{item.grave ? `${item.grave.deceased_name} ${item.grave.deceased_surname}` : "—"}</dd></div>
            <div><dt className="label-soft">Cimitero</dt><dd className="text-navy font-medium">{item.cemetery?.name} — {item.cemetery?.comune} ({item.cemetery?.address})</dd></div>
            <div><dt className="label-soft">Posizione</dt><dd className="text-navy font-medium">Campo {item.grave?.campo || "—"}, fila {item.grave?.fila || "—"}, n. {item.grave?.numero || "—"}</dd></div>
            <div><dt className="label-soft">Fiori</dt><dd className="text-navy font-medium">{item.flower_name || "—"}</dd></div>
            {item.extras?.length > 0 && (
              <div><dt className="label-soft">Extra da consegnare</dt><dd className="text-navy font-medium">{item.extras.map((e) => e.name).join(", ")}</dd></div>
            )}
            <div><dt className="label-soft">Pulizia</dt><dd className="text-navy font-medium">{{ inclusa: "Cura ordinaria (inclusa)", nessuna: "Cura ordinaria (inclusa)", ordinaria: "Pulizia ordinaria", approfondita: "Pulizia approfondita", profonda: "Pulizia specialistica", specialistica: "Pulizia specialistica" }[item.cleaning] || "Cura ordinaria (inclusa)"}</dd></div>
            <div><dt className="label-soft">Data stabilita dall'amministrazione</dt><dd className="text-navy font-medium">{item.scheduled_date ? fmtDate(item.scheduled_date) : "In attesa di programmazione dall'amministrazione"}</dd></div>
            {item.notes && <div><dt className="label-soft">Note del cliente</dt><dd className="text-navy">{item.notes}</dd></div>}
            {item.cemetery?.notes && <div><dt className="label-soft">Note sul cimitero</dt><dd className="text-navy">{item.cemetery.notes}</dd></div>}
          </dl>
          {item.grave?.photo && (
            <div>
              <p className="label-soft">Fotografia del luogo (cliente)</p>
              <img src={item.grave.photo} alt="Luogo" className="w-full rounded-xl object-cover aspect-[4/3]" data-testid="grave-photo" />
            </div>
          )}
          {item.grave?.admin_photo && (
            <div>
              <p className="label-soft">Fotografia di riferimento (amministrazione)</p>
              <img src={item.grave.admin_photo} alt="Riferimento luogo" className="w-full rounded-xl object-cover aspect-[4/3]" data-testid="grave-admin-photo" />
            </div>
          )}
          {item.photos?.length > 1 && (
            <div>
              <p className="label-soft">Altre fotografie del cliente</p>
              <div className="grid grid-cols-2 gap-2">
                {item.photos.map((p, idx) => (
                  <img key={idx} src={p} alt={`Fotografia ${idx + 1}`} className="w-full rounded-xl object-cover aspect-[4/3]" data-testid={`campaign-photo-${idx}`} />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="card-soft p-6">
            <p className="eyebrow mb-3">Posizione approssimativa</p>
            <CemeteryMap image={item.cemetery?.aerial_image} x={item.grave?.map_x} y={item.grave?.map_y} readOnly label={false} />
            {item.grave?.notes && <p className="text-xs text-muted-soft mt-3">Note sul luogo: {item.grave.notes}</p>}
          </div>

          {item.status !== "completata" && (
            <div className="card-ivory p-6 space-y-4" data-testid="pro-actions">
              <p className="eyebrow">Azioni</p>
              {["nuova", "programmata"].includes(item.status) && (
                <button data-testid="take-btn" disabled={busy} onClick={() => act(() => api.post(`/pro/interventions/${id}/take`), "Intervento preso in carico.")} className="btn-primary w-full disabled:opacity-60">
                  Prendi in carico
                </button>
              )}
              <div className="border-t border-[#ece7dd] pt-4 space-y-3">
                <p className="label-soft">Fotografia dell'intervento (obbligatoria per confermare)</p>
                <input data-testid="input-final-photo" type="file" accept="image/*" capture="environment" onChange={onPhoto} className="text-sm text-body" />
                {uploading && <p className="text-xs text-muted-soft">Caricamento…</p>}
                {photo && <img src={photo} alt="Foto finale" data-testid="final-photo-preview" className="w-full rounded-xl" />}
                <textarea data-testid="input-completion-notes" className="input-soft" rows={2} placeholder="Note finali (facoltative)" value={notes} onChange={(e) => setNotes(e.target.value)} />
                <label className="flex items-center gap-2 text-sm text-body" data-testid="confirm-position-label">
                  <input data-testid="confirm-position-check" type="checkbox" checked={confirmPos} onChange={(e) => setConfirmPos(e.target.checked)} className="accent-[#4a7c59] h-4 w-4" />
                  Ho verificato e confermo la posizione del luogo
                </label>
                <button
                  data-testid="complete-btn"
                  disabled={busy || !photo || uploading}
                  onClick={() => act(() => api.post(`/pro/interventions/${id}/complete`, { final_photo: photo, notes, confirm_position: confirmPos }), "Intervento completato. Il cliente ora vede la fotografia.")}
                  className="btn-sage w-full disabled:opacity-60"
                >
                  Conferma lavoro effettuato
                </button>
              </div>
              <div className="border-t border-[#ece7dd] pt-4 space-y-3">
                <p className="label-soft">Problemi con questo intervento?</p>
                <textarea data-testid="input-report" className="input-soft" rows={2} placeholder="Descrivi il problema: la segnalazione arriva all'amministrazione" value={report} onChange={(e) => setReport(e.target.value)} />
                <button
                  data-testid="report-btn"
                  disabled={busy || !report.trim()}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await api.post(`/pro/interventions/${id}/report`, { message: report });
                      toast.success("Segnalazione inviata all'amministrazione.");
                      setReport("");
                    } catch (e) {
                      toast.error(apiError(e));
                    } finally {
                      setBusy(false);
                    }
                  }}
                  className="btn-secondary w-full !py-2.5 disabled:opacity-60"
                >
                  Segnala all'Admin
                </button>
              </div>
            </div>
          )}

          {item.status === "completata" && item.final_photo && (
            <div className="card-ivory p-6 space-y-3" data-testid="completed-section">
              <p className="font-serif text-xl text-navy">{item.exit_title || "Intervento completato"}</p>
              <img src={item.final_photo} alt="Foto finale" className="w-full rounded-xl" />
              <p className="text-xs text-muted-soft">Data programmata: {item.scheduled_date ? fmtDate(item.scheduled_date) : "—"} · Intervento effettuato: {fmtDateTime(item.completed_at)}</p>
              {item.completion_notes && <p className="text-sm text-body">{item.completion_notes}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
